import type { BotIntro, Bot, ChatClient, Message, MessagePart, OAuthConnectorStatus, Session, Source } from './types'
import { getApiClient } from './http'
import { chatTrace } from '@/lib/chatTrace'

type ApiMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE'

interface ApiEndpoint<Args extends unknown[] = []> {
  method: ApiMethod
  path: (...args: Args) => string
}

export const apiRoutes = {
  auth: {
    exchangeCasTicket: { method: 'POST', path: () => '/api/auth/cas/exchange' } satisfies ApiEndpoint,
    ltiStudyLaunch: { method: 'POST', path: () => '/api/auth/lti/study-launch' } satisfies ApiEndpoint,
    ltiLaunch: { method: 'POST', path: () => '/api/auth/lti/launch' } satisfies ApiEndpoint,
    logout: { method: 'POST', path: () => '/api/auth/logout' } satisfies ApiEndpoint,
    mcpAuthorizeStart: { method: 'POST', path: () => '/api/auth/mcp/authorize/start' } satisfies ApiEndpoint,
  },
  bots: {
    greeting: {
      method: 'GET',
      path: (botId: string) => `/api/bots/${botId}/greeting`,
    } satisfies ApiEndpoint<[string]>,
    sessions: {
      method: 'POST',
      path: (botId: string) => `/api/bots/${botId}/sessions`,
    } satisfies ApiEndpoint<[string]>,
    messages: {
      method: 'POST',
      path: (botId: string) => `/api/bots/${botId}/messages`,
    } satisfies ApiEndpoint<[string]>,
    submissions: {
      method: 'POST',
      path: (botId: string) => `/api/bots/${botId}/submissions`,
    } satisfies ApiEndpoint<[string]>,
    sessionMessages: {
      method: 'GET',
      path: (botId: string, sessionId: string, limit = 200) =>
        `/api/bots/${botId}/sessions/${sessionId}/messages?limit=${limit}`,
    } satisfies ApiEndpoint<[string, string, number?]>,
    session: {
      method: 'PATCH',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}`,
    } satisfies ApiEndpoint<[string, string]>,
    getSession: {
      method: 'GET',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}`,
    } satisfies ApiEndpoint<[string, string]>,
    claimAnonymousSession: {
      method: 'POST',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}/claim`,
    } satisfies ApiEndpoint<[string, string]>,
    deleteSession: {
      method: 'DELETE',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}`,
    } satisfies ApiEndpoint<[string, string]>,
  },
  sessions: {
    list: {
      method: 'GET',
      path: (limit = 50, botId?: string) => {
        const params = new URLSearchParams({ limit: String(limit) })
        if (botId) {
          params.set('bot_id', botId)
        }
        return `/api/sessions?${params.toString()}`
      },
    } satisfies ApiEndpoint<[number?, string?]>,
  },
  files: {
    prepareUpload: {
      method: 'POST',
      path: (botId: string, sessionId: string) => `/api/bots/${botId}/sessions/${sessionId}/files/uploads`,
    } satisfies ApiEndpoint<[string, string]>,
    completeUpload: {
      method: 'POST',
      path: (botId: string, sessionId: string, fileId: string) =>
        `/api/bots/${botId}/sessions/${sessionId}/files/${fileId}/complete`,
    } satisfies ApiEndpoint<[string, string, string]>,
    delete: {
      method: 'DELETE',
      path: (botId: string, sessionId: string, fileId: string) =>
        `/api/bots/${botId}/sessions/${sessionId}/files/${fileId}`,
    } satisfies ApiEndpoint<[string, string, string]>,
  },
} as const

interface ApiSession {
  id: string
  title: string
  botId: string
  botName: string
  botType?: Bot['type']
  starred?: boolean
  readOnly?: boolean
  sessionData?: Record<string, unknown>
  oauthConnectors?: OAuthConnectorStatus[]
  createdAt: string
  updatedAt: string
}

interface ApiMessage {
  id: string
  sessionId: string
  role: 'user' | 'assistant'
  textContent: string
  parts?: MessagePart[]
  sources: Source[]
  createdAt: string
  feedbackRating?: FeedbackRating
}

interface ListSessionsResponse {
  sessions: ApiSession[]
  nextCursor?: string
}

export type FeedbackRating = 'good' | 'bad'

export interface BotMessageFeedbackPayload {
  botId: string
  sessionId: string
  messageId: string
  rating: FeedbackRating
  feedback?: string
  notifyMe?: boolean
  email?: string
}

interface GetSessionsOptions {
  botId?: string
  limit?: number
}

interface ListMessagesResponse {
  messages: ApiMessage[]
  nextCursor?: string
}

function toSession(session: ApiSession): Session {
  return {
    id: session.id,
    title: session.title,
    botId: session.botId,
    botName: session.botName,
    botType: session.botType,
    starred: session.starred ?? false,
    readOnly: session.readOnly ?? false,
    sessionData: session.sessionData,
    oauthConnectors: session.oauthConnectors ?? [],
    createdAt: new Date(session.createdAt),
    updatedAt: new Date(session.updatedAt),
  }
}

function toMessage(message: ApiMessage): Message {
  const parts: MessagePart[] = message.parts?.length
    ? message.parts
    : [{
        type: 'text',
        content: message.textContent,
        format: message.role === 'assistant' ? 'markdown' : 'plain',
      } satisfies MessagePart]

  return {
    id: message.id,
    sessionId: message.sessionId,
    role: message.role,
    parts,
    sources: message.sources?.length ? message.sources : undefined,
    createdAt: new Date(message.createdAt),
    feedbackRating: message.feedbackRating,
  }
}

async function requestApi<T>(
  url: string,
  init?: { method?: ApiMethod; body?: unknown; signal?: AbortSignal },
): Promise<T> {
  const response = await getApiClient().request<T>({
    url,
    method: init?.method ?? 'GET',
    data: init?.body,
    signal: init?.signal,
  })
  return response.data
}

export async function fetchBotIntro(botId: string, _baseUrl?: string, signal?: AbortSignal): Promise<BotIntro> {
  const endpoint = apiRoutes.bots.greeting
  const response = await getApiClient().request<BotIntro>({
    url: endpoint.path(botId),
    method: endpoint.method,
    signal,
  })
  return response.data
}

export async function submitBotMessageFeedback(payload: BotMessageFeedbackPayload): Promise<void> {
  await requestApi<{ submitted: boolean }>(
    `/api/bots/${payload.botId}/sessions/${payload.sessionId}/messages/${payload.messageId}/feedback`,
    {
      method: 'POST',
      body: {
        rating: payload.rating,
        feedback: payload.feedback,
        notifyMe: payload.notifyMe ?? false,
        email: payload.email,
      },
    },
  )
}

export function createChatClient(): ChatClient {
  const sessionCache = new Map<string, ApiSession>()

  return {
    getBots(): Bot[] {
      const seen = new Set<string>()
      const bots: Bot[] = []

      for (const session of sessionCache.values()) {
        if (seen.has(session.botId)) continue
        seen.add(session.botId)
        bots.push({
          id: session.botId,
          name: session.botName,
          type: session.botType ?? 'departmental',
        })
      }

      return bots
    },

    getBot(botId: string): Bot | undefined {
      for (const session of sessionCache.values()) {
        if (session.botId === botId) {
          return {
            id: session.botId,
            name: session.botName,
            type: session.botType ?? 'departmental',
          }
        }
      }

      return undefined
    },

    async getSessions(signal?: AbortSignal, options: GetSessionsOptions = {}): Promise<Session[]> {
      const endpoint = apiRoutes.sessions.list
      const path = endpoint.path(options.limit ?? 50, options.botId)
      chatTrace('botApi', 'getSessions.request', { path, botId: options.botId ?? null })
      const data = await requestApi<ListSessionsResponse>(path, {
        method: endpoint.method,
        signal,
      })
      const sessions = data.sessions ?? []
      chatTrace('botApi', 'getSessions.response', {
        count: sessions.length,
        ids: sessions.map(session => session.id),
      })

      for (const session of sessions) {
        sessionCache.set(session.id, session)
      }

      return sessions.map(toSession)
    },

    async getMostRecentSession(botId: string, signal?: AbortSignal): Promise<Session | null> {
      const sessions = await this.getSessions(signal, { botId, limit: 1 })
      return sessions[0] ?? null
    },

    async getMessages(botId: string, sessionId: string, signal?: AbortSignal): Promise<Message[]> {
      const path = apiRoutes.bots.sessionMessages.path(botId, sessionId, 200)
      chatTrace('botApi', 'getMessages.request', { botId, sessionId, path })
      const data = await requestApi<ListMessagesResponse>(
        path,
        {
          method: apiRoutes.bots.sessionMessages.method,
          signal,
        },
      )
      const messages = (data.messages ?? []).map(toMessage)
      chatTrace('botApi', 'getMessages.response', {
        botId,
        sessionId,
        count: messages.length,
        messageIds: messages.map(message => message.id),
      })
      return messages
    },

    async getSession(botId: string, sessionId: string, signal?: AbortSignal): Promise<Session> {
      const data = await requestApi<ApiSession>(
        apiRoutes.bots.getSession.path(botId, sessionId),
        {
          method: apiRoutes.bots.getSession.method,
          signal,
        },
      )
      sessionCache.set(sessionId, data)
      return toSession(data)
    },

    async claimAnonymousSession(botId: string, sessionId: string): Promise<Session> {
      const endpoint = apiRoutes.bots.claimAnonymousSession
      const data = await requestApi<ApiSession>(endpoint.path(botId, sessionId), {
        method: endpoint.method,
      })
      sessionCache.set(sessionId, data)
      return toSession(data)
    },

    async createSession(botId: string, title?: string): Promise<Session> {
      const session = await requestApi<ApiSession>(
        apiRoutes.bots.sessions.path(botId),
        {
          method: apiRoutes.bots.sessions.method,
          body: { title: title ?? 'New Chat' },
        },
      )

      sessionCache.set(session.id, session)
      return toSession(session)
    },

    async sendMessage(_sessionId: string, _parts: MessagePart[]): Promise<Message> {
      throw new Error('[chatClient] Use SseStreamingChatClient for sending messages')
    },

    async deleteSession(botId: string, sessionId: string): Promise<void> {
      await requestApi<{ deleted: boolean }>(
        apiRoutes.bots.deleteSession.path(botId, sessionId),
        {
          method: apiRoutes.bots.deleteSession.method,
        },
      )
      sessionCache.delete(sessionId)
    },

    async greet(botId: string): Promise<{ session: Session; message: Message }> {
      const session = await this.createSession(botId, 'New Chat')
      return {
        session,
        message: {
          id: crypto.randomUUID(),
          sessionId: session.id,
          role: 'assistant',
          parts: [],
          createdAt: new Date(),
        },
      }
    },

    async toggleStar(botId: string, sessionId: string): Promise<{ starred: boolean; limitReached?: boolean }> {
      const cachedSession = sessionCache.get(sessionId)
      const nextStarred = !(cachedSession?.starred ?? false)
      const updatedSession = await requestApi<ApiSession>(
        apiRoutes.bots.session.path(botId, sessionId),
        {
          method: apiRoutes.bots.session.method,
          body: { starred: nextStarred },
        },
      )

      sessionCache.set(sessionId, {
        ...(cachedSession ?? updatedSession),
        ...updatedSession,
        starred: updatedSession.starred ?? nextStarred,
      })
      return { starred: updatedSession.starred ?? nextStarred }
    },

    async updateSession(botId: string, sessionId: string, updates: { title?: string; starred?: boolean }): Promise<Session> {
      const updatedSession = await requestApi<ApiSession>(
        apiRoutes.bots.session.path(botId, sessionId),
        {
          method: apiRoutes.bots.session.method,
          body: updates,
        },
      )
      sessionCache.set(sessionId, updatedSession)
      return toSession(updatedSession)
    },
  }
}
